/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import logger from 'js/app/loggerSingleton';

import { breakpoints } from '@coursera/cds-core';

import HorizontalSpacing from 'bundles/browse-common/components/HorizontalSpacing';
import PageConfigComponentRendererWrapper from 'bundles/browse-common/components/PageConfigComponentRendererWrapper';
import BrowseFAQsModuleRenderer from 'bundles/browse-common/renderers/BrowseFAQsModuleRenderer';
import AnyCollectionRenderer from 'bundles/browse-common/renderers/ComplexCollectionRenderer/AnyCollectionRenderer';
import DegreeCollectionRenderer from 'bundles/browse-common/renderers/DegreeCollectionRenderer';
import DomainListRenderer from 'bundles/browse-common/renderers/DomainListRenderer';
import DomainValuePropositionRenderer from 'bundles/browse-common/renderers/DomainValuePropositionRenderer';
import ExploreRolesRenderer from 'bundles/browse-common/renderers/ExploreRolesRenderer';
import LoggedOutProgressiveOnboardingRenderer from 'bundles/browse-common/renderers/LoggedOutProgressiveOnboardingRenderer';
import RelatedSkillsRenderer from 'bundles/browse-common/renderers/RelatedSkillsRenderer';
import TopPartnersComponentRenderer from 'bundles/browse-common/renderers/TopPartnersComponentRenderer';
import CoachOnboardingBannerModuleRenderer from 'bundles/page-config-common/module-renderers/CoachOnboardingBannerModuleRenderer';
import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import SearchFrame from 'bundles/search-common/components/SearchFrame';
import { FILTER_TYPE, PAGINATION_TYPE } from 'bundles/search-common/constants/filter';

type Props = {
  pageConfigComponents: Array<PageConfigComponent>;
};

// Standarize all top margins to 0 and bottom margins to 32px / 24px
const styles = {
  standardMarginBottom: css`
    margin-bottom: var(--cds-spacing-400);
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  domainList: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  valueProposition: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;

      & > div > div {
        & > div:nth-of-type(1) {
          padding-top: 0;
          padding-bottom: 0;
        }

        & > div:nth-of-type(2) {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }
  `,
  coachOnboardingBanner: css`
    & > div > div {
      margin: 0;
      width: 100%;

      & > div {
        margin: 0;
      }
    }
  `,
  relatedSkills: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  degreeCollection: css`
    & > div:nth-of-type(2) > div {
      padding-top: 0 !important;
      padding-bottom: 0 !important;

      & > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div:nth-of-type(1) {
          padding-top: 0;
          padding-bottom: 0;
        }

        & > div:nth-of-type(2) {
          padding-top: 0;
          padding-bottom: 0;

          & > div {
            margin-top: 0;
          }
        }
      }
    }
  `,
  exploreRoles: css`
    section > div {
      margin-top: 0;
      margin-bottom: 0;

      & > div {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    }
  `,

  searchFrame: css`
    section {
      padding-top: 0;
      padding-bottom: 0;

      & > div > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div {
          margin-top: 0;
          margin-bottom: 0;

          & > div:nth-of-type(2) {
            padding-bottom: 0;
          }

          & > div:nth-of-type(3) {
            padding-bottom: 0;
          }
        }
      }
    }
  `,
  topPartners: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;

      & > div > div {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  `,
  faq: css`
    & > div > div > div:nth-of-type(2) > div {
      padding-top: 0;
      padding-bottom: 0;

      & > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div {
          padding-top: 0;
          padding-bottom: 0;

          & > div {
            margin-top: 0;
            margin-bottom: 0;

            & > div:nth-of-type(1) {
              padding-top: 0;
            }

            & > div:nth-of-type(2) {
              padding-bottom: 0;
            }

            & > div:nth-of-type(3) {
              padding-bottom: 0;
            }
          }
        }
      }
    }
  `,
  loggedOutProgressiveOnboarding: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
};

function renderComponent(pageConfigComponent: PageConfigComponent, pageConfigComponents: PageConfigComponent[]) {
  switch (pageConfigComponent.__typename) {
    case 'PageConfig_BrowseValuePropositionComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.valueProposition]}>
          <HorizontalSpacing>
            <DomainValuePropositionRenderer />
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_BrowseDomainListComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.domainList]}>
          <HorizontalSpacing>
            <DomainListRenderer pageConfig={pageConfigComponent} />
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_BrowseRelatedSkillsComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.relatedSkills]}>
          <HorizontalSpacing>
            <RelatedSkillsRenderer pageConfig={pageConfigComponent} />
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_BrowseDegreeCollectionComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.degreeCollection]}>
          <DegreeCollectionRenderer />
        </div>
      );
    }
    case `PageConfig_CoachOnboardingBanner`: {
      return (
        <div css={[styles.standardMarginBottom, styles.coachOnboardingBanner]}>
          <CoachOnboardingBannerModuleRenderer hideWhenOnboardingCompleted={true} />
        </div>
      );
    }
    case `PageConfig_BrowseLoggedOutProgressiveOnboardingComponent`: {
      return (
        <div css={[styles.standardMarginBottom, styles.loggedOutProgressiveOnboarding]}>
          <HorizontalSpacing>
            <LoggedOutProgressiveOnboardingRenderer />
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_ExploreRolesComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.exploreRoles]}>
          <ExploreRolesRenderer pageConfigComponent={pageConfigComponent} />
        </div>
      );
    }
    case 'PageConfig_CollectionComponent': {
      return (
        <AnyCollectionRenderer pageComponentList={pageConfigComponents} pageConfigComponent={pageConfigComponent} />
      );
    }
    case 'PageConfig_TopPartnersComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.topPartners]}>
          <HorizontalSpacing>
            <TopPartnersComponentRenderer />
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_SearchFrameComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.searchFrame]}>
          <SearchFrame
            config={{
              paginationType: PAGINATION_TYPE.PAGINATED,
              enableFilters: true,
              filterType: FILTER_TYPE.INLINE,
              showSortBy: true,
              showRelatedQueries: true,
              showCplusUpsell: true,
              isSEOEntityPage: false,
              enableFeaturedSnippet: false,
              enableSchemaMarkup: false,
              enableSEOMetatags: false,
              enableScrollToResultsOnPageChange: true,
            }}
          />
        </div>
      );
    }
    case 'PageConfig_FAQComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.faq]}>
          <BrowseFAQsModuleRenderer />
        </div>
      );
    }
    default: {
      logger.warn('unexpected pageConfigComponent', pageConfigComponent);
      return null;
    }
  }
}

const BrowsePageConfigComponentRenderer = (props: Props) => {
  const { pageConfigComponents } = props;

  return (
    <PageConfigComponentRendererWrapper pageConfigComponents={pageConfigComponents} renderComponent={renderComponent} />
  );
};

export default BrowsePageConfigComponentRenderer;
